/**
 * Landing buy box star rating.
 *
 * Mirrors the REVIEWS section further down the same page rather than rendering
 * a second Stamped badge of its own. That section's widget is handed an empty
 * product id (a page template has no `product`), so Stamped answers it with
 * the store-wide totals - 716 reviews rather than the ~377 on any single
 * product. Reading the number back out of that widget is the only way the two
 * are guaranteed to agree.
 *
 * Stamped's summary markup, confirmed against the overrides in
 * sections/pdp-app-reviews.liquid:
 *
 *   .stamped-summary-text-1   the average, e.g. "4.9"
 *   .stamped-summary-text     the caption, e.g. "Based on 716 reviews"
 *
 * The row ships hidden and empty. If Stamped never reports a count it stays
 * that way - an empty five-star row above the price is worse than no row. The
 * wrapper is a fixed height in CSS, so revealing it never moves the price.
 */
(function () {
  'use strict';

  var SETTLE_MS = 15000;
  var SCRIPT = document.currentScript;

  function config(name, fallback) {
    if (!SCRIPT) return fallback;
    var value = SCRIPT.getAttribute('data-' + name);
    return value === null || value === '' ? fallback : value;
  }

  var HALF_FILL = config('half-star-fill', 'transparent');
  var LABEL = config('label', 'Reviews');

  function firstNumber(text) {
    var match = (text || '').replace(/,/g, '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : NaN;
  }

  /**
   * Pulls the rating and review count out of Stamped's summary block.
   * Returns null until both are present and the count is non-zero.
   */
  function readSummary(widget) {
    var ratingEl = widget.querySelector('.stamped-summary-text-1');
    var captionEl = widget.querySelector('.stamped-summary-text');
    if (!ratingEl || !captionEl) return null;

    var rating = firstNumber(ratingEl.textContent);
    var count = firstNumber(captionEl.textContent);
    if (isNaN(rating) || isNaN(count) || count <= 0) return null;

    return { rating: rating, count: Math.round(count) };
  }

  function paint(row, summary) {
    // Fill each star from the rating, to the nearest half.
    var tenths = Math.min(Math.round(summary.rating * 10), 50);
    var stars = row.querySelectorAll('.fs-landing-reviews__star path');

    Array.prototype.forEach.call(stars, function (star, index) {
      var fullAt = (index + 1) * 10;
      var fill = 'transparent';
      if (tenths >= fullAt) {
        fill = 'currentColor';
      } else if (tenths >= fullAt - 5) {
        fill = HALF_FILL;
      }
      star.setAttribute('fill', fill);
    });

    var printed = summary.count.toLocaleString('en-US');
    row.querySelector('.fs-landing-reviews__count').textContent =
      printed + ' ' + LABEL;
    row.querySelector('[data-fs-reviews-a11y]').textContent =
      'Rated ' + summary.rating + ' out of 5 from ' + printed + ' ' +
      LABEL.toLowerCase() + '. Jump to the reviews.';

    row.hidden = false;
  }

  /**
   * Scrolls to the REVIEWS section. The section's own id is generated by
   * Shopify and unknown to the buy box, so walk up from the Stamped widget we
   * already found. Honours reduced-motion.
   */
  function wireScroll(row, widget) {
    var target =
      widget.closest('.pdp-app-reviews') ||
      widget.closest('.shopify-section') ||
      widget;

    row.addEventListener('click', function () {
      var reduced =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      target.scrollIntoView({
        behavior: reduced ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  }

  function init() {
    var row = document.querySelector('[data-fs-reviews]');
    var widget = document.getElementById('stamped-main-widget');
    if (!row || !widget) return;

    var done = false;

    var attempt = function () {
      if (done) return true;
      var summary = readSummary(widget);
      if (!summary) return false;
      done = true;
      paint(row, summary);
      return true;
    };

    wireScroll(row, widget);

    if (attempt()) return;

    var observer = new MutationObserver(function () {
      if (attempt()) observer.disconnect();
    });

    observer.observe(widget, { childList: true, subtree: true });

    // Stamped may never load - blocked script, app removed, or the REVIEWS
    // section taken off this template. Stop watching rather than leaving an
    // observer alive for the whole session.
    window.setTimeout(function () {
      observer.disconnect();
    }, SETTLE_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
